import React, { useState, useEffect, useRef } from "react";
import api from "../api";

function timeAgo(dateString) {
  const now = new Date();
  const date = new Date(dateString);
  const seconds = Math.floor((now - date) / 1000);

  if (seconds < 60) return "À l'instant";
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `Il y a ${minutes} min`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `Il y a ${hours}h`;
  const days = Math.floor(hours / 24);
  return `Il y a ${days}j`;
}

function StatusBadge({ status }) {
  const styles = {
    sent: "bg-emerald-50 text-emerald-700 border-emerald-200",
    visit: "bg-amber-50 text-amber-700 border-amber-200",
    connect: "bg-emerald-50 text-emerald-700 border-emerald-200",
    invitation: "bg-blue-50 text-blue-700 border-blue-200",
    delivered: "bg-emerald-50 text-emerald-700 border-emerald-200",
    failed: "bg-rouge-soft text-rouge border-rouge/20",
    pending: "bg-amber-50 text-amber-700 border-amber-200",
  };
  const labels = {
    sent: "Envoyé",
    visit: "Visite",
    connect: "Connexion",
    invitation: "Invitation",
    delivered: "Délivré",
    failed: "Échoué",
    pending: "En attente",
  };

  return (
    <span
      className={`text-xs font-medium px-2 py-0.5 rounded-full border ${
        styles[status] || styles.pending
      }`}
    >
      {labels[status] || status}
    </span>
  );
}

export default function ActivityFeed() {
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);
  const intervalRef = useRef(null);

  const fetchActivity = async () => {
    try {
      const data = await api.get("/api/dashboard/activity");
      setActivities(Array.isArray(data) ? data : data.activity || []);
    } catch {
      /* ignore */
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchActivity();
    intervalRef.current = setInterval(fetchActivity, 30000);
    return () => clearInterval(intervalRef.current);
  }, []);

  if (loading) {
    return (
      <div className="card p-6">
        <h2 className="type-section-title text-ink mb-4">
          Activité récente
        </h2>
        <div className="space-y-3">
          {[1, 2, 3].map((i) => (
            <div key={i} className="animate-pulse flex items-center gap-3">
              <div className="w-8 h-8 bg-ink/5 rounded-full" />
              <div className="flex-1">
                <div className="h-3 bg-ink/5 rounded w-3/4 mb-2" />
                <div className="h-3 bg-ink/5 rounded w-1/2" />
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="card p-6">
      <h2 className="type-section-title text-ink mb-4">
        Activité récente
      </h2>

      {activities.length === 0 ? (
        <div className="text-center py-8">
          <svg
            className="w-12 h-12 text-ink/15 mx-auto mb-3"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={1.5}
              d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z"
            />
          </svg>
          <p className="text-sm text-mute">
            L'IA n'a pas encore envoyé de messages
          </p>
        </div>
      ) : (
        <div className="space-y-1">
          {activities.map((item, index) => (
            <div
              key={index}
              className="flex items-start gap-3 p-3 rounded-lg hover:bg-ink/3 transition-colors"
            >
              {/* Avatar placeholder */}
              <div className="w-8 h-8 rounded-full bg-rouge-soft flex items-center justify-center flex-shrink-0">
                <span className="text-xs font-semibold text-rouge">
                  {(item.contact_name || "?").charAt(0).toUpperCase()}
                </span>
              </div>

              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-0.5">
                  <span className="text-sm font-medium text-ink truncate">
                    {item.contact_name}
                  </span>
                  <StatusBadge status={item.status} />
                </div>
                <p className="text-sm text-mute line-clamp-1">
                  {item.message_sent}
                </p>
                <div className="flex items-center gap-2 mt-1">
                  <span className="type-label-accent">
                    {item.segment_name}
                  </span>
                  <span className="text-xs text-ink/20">|</span>
                  <span className="text-xs text-mute">
                    {timeAgo(item.created_at)}
                  </span>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
